/**
 * TimeWheelPair — two side-by-side PICKUP / RETURN fields, each with
 * its own inline three-column scroll wheel (hour · minute · AM/PM).
 * Six independent scrollers on screen, always visible — no
 * tap-to-reveal, no separate drum below.
 *
 * The SELECTED field carries the orange ring; the unselected field is
 * a plain surface but its wheels are still visible and scrollable.
 *
 * Values obeyed exactly from step1e.html:
 *   - .wheel padding 11px 7px 9px, radius 20, surface rgba(255,255,255,.055)
 *   - .wheel.sel background rgba(237,102,3,.13) + inset 0 0 0 1px rgba(237,102,3,.5)
 *   - .roll height 108px, .band top 36px, height 36px, radius 10, bg rgba(255,255,255,.10)
 *   - .col .r height 36px line-height 36px, dim rgba(255,255,255,.26), scale .88, 13px
 *   - .col .r.sel #fff, 800, 18px, no transform, letter-spacing -.3px
 *
 * Constraints (operating hours 4:00 AM – 8:00 PM, return-after-pickup)
 * are enforced at commit time: if a scroll would result in an invalid
 * state, we snap the offending column back rather than propagate.
 *
 * Every column has `touch-action: pan-y` + `overscroll-behavior: contain`
 * so a scroll gesture inside a column never propagates to the sheet or
 * a neighboring column.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

// Config — the only source of operating hours. AM/PM eligibility is
// derived per hour12 from this.
export const START_HOUR = 4;  // 4:00 AM inclusive
export const END_HOUR   = 20; // 8:00 PM inclusive (no minutes past)
const ROW = 36;
const VISIBLE = 3;

// Standard 12-hour clock face — both hour wheels show 1-12. AM/PM
// column + `ampmValid()` gate combinations that fall outside operating
// hours (4 AM – 8 PM) so we can keep the hour picker predictable while
// still enforcing the service window.
const HOURS   = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = [0, 15, 30, 45];
const AMPMS   = ["AM", "PM"];

const pad2 = (n) => String(n).padStart(2, "0");

// (hour12, minute, ampm) → 24h "HH:MM", or null if out of range.
export function toHHMM(hour12, minute, ampm) {
  let h = Number(hour12);
  const m = Number(minute);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (ampm === "AM") { if (h === 12) h = 0; }
  else               { if (h !== 12) h += 12; }
  if (h < START_HOUR) return null;
  if (h > END_HOUR)   return null;
  if (h === END_HOUR && m > 0) return null;
  return `${pad2(h)}:${pad2(m)}`;
}

// "HH:MM" → { hour12, minute, ampm }, with a sensible fallback.
function fromHHMM(hhmm, fb) {
  const [hS, mS] = String(hhmm || "").split(":");
  const h = parseInt(hS, 10);
  const m = parseInt(mS, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return fb;
  return { hour12: ((h + 11) % 12) + 1, minute: m, ampm: h >= 12 ? "PM" : "AM" };
}

function Column({ options, value, onChange, formatter = (v) => v, disabledFor = () => false, testId }) {
  const ref = useRef(null);
  const snapTimer = useRef(null);
  const [liveIdx, setLiveIdx] = useState(() => Math.max(0, options.indexOf(value)));

  // Sync scroll position to the incoming value.
  useEffect(() => {
    const idx = Math.max(0, options.indexOf(value));
    const el = ref.current;
    if (!el) return;
    const target = idx * ROW;
    if (Math.abs(el.scrollTop - target) > 2) el.scrollTop = target;
    setLiveIdx(idx);
  }, [value, options]);

  const handleScroll = () => {
    const el = ref.current;
    if (!el) return;
    const raw = el.scrollTop / ROW;
    const nearest = Math.max(0, Math.min(options.length - 1, Math.round(raw)));
    if (nearest !== liveIdx) setLiveIdx(nearest);
    if (snapTimer.current) clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => {
      let idx = nearest;
      let guard = 0;
      while (disabledFor(options[idx]) && guard < options.length) {
        idx = (idx + 1) % options.length;
        guard++;
      }
      el.scrollTo({ top: idx * ROW, behavior: "smooth" });
      setLiveIdx(idx);
      const next = options[idx];
      if (next !== value) {
        try { navigator.vibrate?.(6); } catch (_) { /* noop */ }
        onChange?.(next);
      }
    }, 130);
  };

  // Show at most VISIBLE=3 rows at once (center is the selected band).
  // We render one row above + below so momentum-scroll has content to
  // scroll into; the container clips to VISIBLE * ROW.
  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      className="relative overflow-y-auto no-scrollbar flex-1"
      style={{
        height: VISIBLE * ROW,
        touchAction: "pan-y",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        scrollSnapType: "y mandatory",
      }}
      data-testid={testId}
    >
      <div style={{ height: ROW }} aria-hidden="true" />
      {options.map((opt, i) => {
        const isCenter = i === liveIdx;
        const disabled = disabledFor(opt);
        return (
          <div
            key={`${opt}-${i}`}
            onClick={() => {
              if (disabled) return;
              ref.current?.scrollTo({ top: i * ROW, behavior: "smooth" });
            }}
            className="flex items-center justify-center tabular-nums cursor-pointer"
            style={{
              height: ROW,
              lineHeight: `${ROW}px`,
              scrollSnapAlign: "center",
              scrollSnapStop: "always",
              fontFamily: "var(--font-display)",
              fontWeight: isCenter ? 800 : 700,
              fontSize: isCenter ? 18 : 13,
              color: isCenter ? "#fff" : "rgba(255,255,255,0.26)",
              transform: isCenter ? "none" : "scale(0.88)",
              letterSpacing: isCenter ? "-0.3px" : "0",
              opacity: disabled ? 0.18 : 1,
            }}
            data-value={String(opt)}
          >
            {formatter(opt)}
          </div>
        );
      })}
      <div style={{ height: ROW }} aria-hidden="true" />
    </div>
  );
}

function ampmValid(hour12) {
  const h = Number(hour12);
  const amHour = h === 12 ? 0 : h;
  const pmHour = h === 12 ? 12 : h + 12;
  return {
    AM: amHour >= START_HOUR && amHour < END_HOUR,
    PM: pmHour <= END_HOUR,
  };
}

// "HH:MM" comparison — returns true if a is strictly before b.
// Normalize an HH:MM-ish value that may be a "HH:MM-HH:MM" window
// range string coming from legacy state. We always compare on the
// start-time of the range so the return-after-pickup guard doesn't
// silently reject every scroll (NaN comparisons return false).
function normalizeHHMM(v) {
  if (!v) return v;
  return String(v).split("-")[0];
}

function isBefore(a, b) {
  const av = normalizeHHMM(a);
  const bv = normalizeHHMM(b);
  if (!av || !bv) return false;
  const [ah, am] = av.split(":").map(Number);
  const [bh, bm] = bv.split(":").map(Number);
  if (Number.isNaN(ah) || Number.isNaN(am) || Number.isNaN(bh) || Number.isNaN(bm)) return false;
  return ah * 60 + am < bh * 60 + bm;
}

/** Single wheel field — the PICKUP or RETURN card. */
function WheelField({ label, value, onChange, selected, onSelect, testId, otherValue, kind, minValue, maxValue }) {
  const parts = useMemo(() => fromHHMM(value, { hour12: kind === "pickup" ? 7 : 5, minute: 0, ampm: kind === "pickup" ? "AM" : "PM" }), [value, kind]);
  const { hour12, minute, ampm } = parts;

  // Two constraint layers:
  //   1. Operating hours (START_HOUR..END_HOUR, inclusive lower / no
  //      minutes past END_HOUR) — enforced inside `toHHMM`.
  //   2. Arrival-anchoring narrowed range [minValue, maxValue] (HH:MM
  //      inclusive) — computed by the parent from live drive-time so
  //      the resulting pickup / return window never falls outside
  //      operating hours. Undefined min/max means "no narrowing".
  const inNarrow = (hhmm) => {
    if (!hhmm) return false;
    if (minValue && hhmm < normalizeHHMM(minValue)) return false;
    if (maxValue && hhmm > normalizeHHMM(maxValue)) return false;
    return true;
  };
  const validate = (h, m, ap) => {
    const hhmm = toHHMM(h, m, ap);
    if (!hhmm) return null;
    if (!inNarrow(hhmm)) return null;
    return hhmm;
  };

  // Emit a new HH:MM on any column commit — with validation. If the
  // combination violates operating hours OR the narrowed range OR
  // return-before-pickup, we snap by ignoring the emit (the column
  // onChange still fires, but parent-side validation will land on the
  // last-good value).
  const emit = (nextHour12, nextMinute, nextAmpm) => {
    let hhmm = validate(nextHour12, nextMinute, nextAmpm);
    if (!hhmm) {
      // Try flipping ampm to the OTHER side to salvage.
      const alt = validate(nextHour12, nextMinute, nextAmpm === "AM" ? "PM" : "AM");
      if (alt) hhmm = alt;
    }
    if (!hhmm) return;
    // return-after-pickup enforcement
    if (kind === "return" && otherValue && !isBefore(otherValue, hhmm)) return;
    if (kind === "pickup" && otherValue && !isBefore(hhmm, otherValue)) return;
    onChange?.(hhmm);
  };

  // Column-level disabledFor predicates — grey out non-selectable rows.
  // Hour column: hour is disabled iff NO (minute, ampm) combo yields a
  //   value inside operating hours AND the narrowed range.
  const hourDisabled = (h) => {
    for (const mm of MINUTES) {
      for (const ap of AMPMS) {
        if (validate(h, mm, ap)) return false;
      }
    }
    return true;
  };
  // Minute column: disabled iff no ampm combo with the CURRENT hour is
  //   valid (matches the visual expectation that unreachable minutes
  //   dim out — even if a different hour could make them valid).
  const minuteDisabled = (mm) => {
    for (const ap of AMPMS) {
      if (validate(hour12, mm, ap)) return false;
    }
    return true;
  };
  // AM/PM column: disabled iff switching to that ampm with the
  //   CURRENT hour+minute yields out-of-range.
  const ampmDisabled = (v) => !validate(hour12, minute, v);

  return (
    <button
      type="button"
      onClick={() => onSelect?.()}
      className="flex-1 min-w-0 relative text-left"
      style={{
        background: selected ? "rgba(237,102,3,0.13)" : "rgba(255,255,255,0.055)",
        borderRadius: 20,
        padding: "11px 7px 9px",
        boxShadow: selected
          ? "0 12px 30px rgba(237,102,3,0.24)"
          : "0 6px 20px rgba(0,0,0,0.30)",
        outline: selected ? "1px solid rgba(237,102,3,0.5)" : "none",
        outlineOffset: "-1px",
      }}
      data-testid={testId}
      data-selected={selected}
    >
      <div
        className="text-center mb-[7px]"
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 9,
          letterSpacing: "1.8px",
          color: selected ? "rgba(255,255,255,0.72)" : "#7b87a6",
        }}
      >
        {label}
      </div>
      <div className="relative flex" style={{ height: 108, overflow: "hidden", gap: 2 }}>
        {/* Highlight band spanning all three columns. */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: 2, right: 2, top: 36, height: 36,
            borderRadius: 10, background: "rgba(255,255,255,0.10)",
          }}
          aria-hidden="true"
        />
        <Column
          options={HOURS}
          value={hour12}
          onChange={(v) => emit(v, minute, ampm)}
          formatter={(v) => String(v)}
          disabledFor={hourDisabled}
          testId={`${testId}-hour`}
        />
        <Column
          options={MINUTES}
          value={minute}
          onChange={(v) => emit(hour12, v, ampm)}
          formatter={(v) => pad2(v)}
          disabledFor={minuteDisabled}
          testId={`${testId}-minute`}
        />
        <Column
          options={AMPMS}
          value={ampm}
          onChange={(v) => emit(hour12, minute, v)}
          formatter={(v) => v}
          disabledFor={ampmDisabled}
          testId={`${testId}-ampm`}
        />
      </div>
    </button>
  );
}

/**
 * Composed pair. Parent supplies pickup + return HH:MM strings and setters.
 *
 * `pickupMin` / `pickupMax` and `returnMin` / `returnMax` narrow each
 * wheel's selectable range so an ARRIVE BY that would push the
 * computed pickup below 4:00 AM (or a LEAVE WORK that would push the
 * return past 8:00 PM) is blocked at the row level.
 */
export default function TimeWheelPair({
  pickup, setPickup, returnv, setReturn,
  pickupMin, pickupMax, returnMin, returnMax,
  testId = "wheel-pair",
}) {
  const [selected, setSelected] = useState("pickup");
  return (
    <div className="flex gap-3" data-testid={testId}>
      <WheelField
        kind="pickup"
        label="ARRIVE BY"
        value={pickup}
        onChange={setPickup}
        selected={selected === "pickup"}
        onSelect={() => setSelected("pickup")}
        testId={`${testId}-pickup`}
        otherValue={returnv}
        minValue={pickupMin}
        maxValue={pickupMax}
      />
      <WheelField
        kind="return"
        label="LEAVE WORK"
        value={returnv}
        onChange={setReturn}
        selected={selected === "return"}
        onSelect={() => setSelected("return")}
        testId={`${testId}-return`}
        otherValue={pickup}
        minValue={returnMin}
        maxValue={returnMax}
      />
    </div>
  );
}
